// api.ts — the console's single door to /api/platform/*.
//
// Every screen in this tree calls through here rather than apiCallEx directly,
// for one reason: REASON PROPAGATION. The backend requires an operator reason on
// cross-tenant and sensitive mutations (routes/platformAdmin.js `opReason`, which
// reads body.reason → query.reason → the x-operator-reason header). Scattering
// that across a dozen screens is how a mutation eventually ships without one and
// lands an unattributed row in operator_audit.
//
// So: `pmutate` REQUIRES a reason argument at the type level. A screen physically
// cannot fire a mutation without passing one.

import { apiCallEx, type ApiResult } from "../../apiClient";

const BASE = "/platform";

/** Read. No reason required — GETs are not audited mutations. */
export async function pget(path: string, query?: Record<string, string | number | undefined>): Promise<ApiResult> {
  const qs = query
    ? "?" + Object.entries(query)
        .filter(([, v]) => v !== undefined && v !== "")
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join("&")
    : "";
  return apiCallEx(`${BASE}${path}${qs}`);
}

/**
 * Mutate. `reason` is a required positional argument, not an optional field on
 * an options bag — the compiler enforces what the audit trail depends on.
 *
 * Sent BOTH in the body and as `x-operator-reason`, because the backend's
 * precedence is body → query → header, and some of these routes take no body at
 * all (the lifecycle POSTs). Sending both means one code path covers every route.
 */
export async function pmutate(
  method: "POST" | "PUT" | "DELETE",
  path: string,
  reason: string,
  body?: Record<string, unknown>,
): Promise<ApiResult> {
  const trimmed = (reason || "").trim();
  // on401 "return": a mutation must never be ended by a silent page reload. The
  // caller has a dialog open and a change the user believes is being applied;
  // navigating away turns "your session expired, nothing was saved" into "it
  // just didn't work", which is unfalsifiable from the user's side. Reads keep
  // the default reload — there is nothing in flight to explain.
  return apiCallEx(`${BASE}${path}`, {
    method,
    headers: { "x-operator-reason": trimmed },
    body: JSON.stringify({ ...(body || {}), reason: trimmed }),
  }, "return");
}

/**
 * Turn a failed ApiResult into something an operator can act on.
 *
 * The backend returns `{ error, message }` and the `message` is written for a
 * human (see the operator-SSO handlers). Prefer it; fall back to the error code;
 * never show a bare "Request failed" when the server explained itself.
 */
export function perr(r: ApiResult, fallback = "Request failed"): string {
  if (r.status === 0) return "Network error — the console could not reach the API.";
  // Says both halves out loud: the session is gone AND the change did not happen.
  // Stating only the first leaves the user to guess about the second, and the
  // guess they make is "it saved".
  if (r.status === 401) return "Your session expired and this change was NOT saved. Sign in again and re-apply it.";
  if (r.status === 403) return "Forbidden — this action requires operator access on the admin host.";
  const d = r.data || {};
  return d.message || d.error || `${fallback} (HTTP ${r.status})`;
}
